import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { quoteApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Button, ConfirmDialog, SplitButton } from '../ui.tsx';
import { publishSendOutcome, sendOutcomeEpoch, type SendOutcome } from '../../hooks/use-send-outcome.ts';
import { canTransition, type QuoteStatus } from '@shared/constants/index.ts';
import type { Quote, QuoteSummary } from '@shared/types/index.ts';

type Channel = 'email' | 'text' | 'both';

type SendResult =
  | { channel: 'email'; recipient: string; delivered: boolean }
  | { channel: 'text'; recipient: string; delivered: boolean; deferred: boolean }
  | {
      channel: 'both';
      emailRecipient: string;
      textRecipient: string;
      emailDelivered: boolean;
      textDelivered: boolean;
      textDeferred: boolean;
    };

/**
 * "Get this in front of the customer", offered from wherever a quote is
 * listed — one button standing in for the editor's two separate Email and
 * Text sections, since a list row has no room for either one's form.
 *
 * There is no channel a list row can call the obvious default: it carries
 * neither the customer's email nor their phone, only whether the company has
 * texting switched on at all. So the button picks the most universal channel
 * — email — as the one click, and folds the rest into the menu next to it
 * rather than guessing further. Which address or number actually gets used is
 * the same fallback `/send` and `/send-text` have always used: the quote's
 * own snapshot from when it was created, not a fresh customer lookup — the
 * editor shows and lets staff override that address before sending; this
 * button cannot override it, so it names it instead: the click reads the
 * quote fresh and asks, with the address or number in the question, before
 * anything goes out. One tap in a row of Download, Copy and Delete is too easy
 * to land by accident for a message that cannot be recalled.
 *
 * What happens after the click is published to `use-send-outcome.ts` rather
 * than kept as this component's own state — see that module's comment for
 * why a row that just changed status is not a safe place to hold the
 * message about it, and for why every publish here is stamped with an epoch
 * read before the request went out.
 */
export function SendQuoteButton({ quote, className }: { quote: QuoteSummary; className?: string }) {
  const { t } = useTranslation();
  const { can, company } = useSession();
  const queryClient = useQueryClient();

  const status = quote.status as QuoteStatus;
  // Mirrors the editor's own `canEmail`: every status a send can still move
  // out of draft or resend from, and nothing a customer has already answered
  // — the server refuses those transitions (an expired one included, unless
  // it is being canceled), and a button that cannot work is worse than none.
  // `viewed` means the customer already opened a sent quote. Resending is
  // still the right action, and the server reuses the live link.
  const canSend =
    (status === 'sent' || status === 'viewed' || canTransition(status, 'sent') || canTransition(status, 'ready')) &&
    !quote.isExpired &&
    can('quotes:write');
  const companyCanText = Boolean(company?.smsConfigured) && Boolean(company?.smsRemindersEnabled);
  const alreadySent = status === 'sent' || status === 'viewed';
  /** The send being asked about, with the quote as it was read for the question. */
  const [asking, setAsking] = useState<{ channel: Channel; fresh: Quote } | null>(null);

  /** What a text's own outcome fields say, in the order the editor checks them. */
  function textOutcome(recipient: string, delivered: boolean, deferred: boolean): SendOutcome {
    if (deferred) return { tone: 'warning', text: t('quotes.list.sendTextDeferred', { recipient }) };
    if (!delivered) return { tone: 'error', text: t('quotes.list.sendTextNotDelivered', { recipient }) };
    return { tone: 'success', text: t('quotes.list.sendSuccessText', { recipient }) };
  }

  /**
   * A combined send has two independent channels, each with its own way to
   * fall short, so every combination gets its own branch rather than a
   * priority order that could report one channel's true state using the
   * wrong copy — a deferred text is not "not delivered", and it is not
   * "delivered" either.
   */
  function bothOutcome(
    emailRecipient: string,
    textRecipient: string,
    emailDelivered: boolean,
    textDelivered: boolean,
    textDeferred: boolean,
  ): SendOutcome {
    if (emailDelivered && textDelivered) {
      return { tone: 'success', text: t('quotes.list.sendSuccessBoth', { emailRecipient, textRecipient }) };
    }
    if (emailDelivered && textDeferred) {
      return { tone: 'warning', text: t('quotes.list.sendBothTextDeferred', { emailRecipient, textRecipient }) };
    }
    if (emailDelivered) {
      return { tone: 'warning', text: t('quotes.list.sendBothTextNotDelivered', { emailRecipient, textRecipient }) };
    }
    if (textDelivered) {
      return { tone: 'warning', text: t('quotes.list.sendBothEmailFailed', { emailRecipient, textRecipient }) };
    }
    if (textDeferred) {
      return {
        tone: 'warning',
        text: t('quotes.list.sendBothEmailFailedTextDeferred', { emailRecipient, textRecipient }),
      };
    }
    return { tone: 'error', text: t('quotes.list.sendBothNeitherDelivered', { emailRecipient, textRecipient }) };
  }

  /**
   * Reads the quote fresh before acting on it, the same caution
   * `ScheduleQuoteButton` uses: a list row can be half a minute old, and
   * sending against a version it no longer has is a conflict this button
   * would rather avoid than retry.
   *
   * No client-side expiry check here on purpose — an earlier version of this
   * button had one, but `GET /quotes/:quoteId` resolves the *company's*
   * timezone rather than the quote's own location, unlike the send routes
   * themselves (`currentQuoteTimezone`), so it could refuse a send the server
   * would still allow for a location whose day starts later. All three send
   * routes now run their own expiry check even for the `sent → sent` resend
   * `assertCanTransition` otherwise treats as a no-op, so the server is the
   * one authority worth trusting here; its refusal reaches `onError` like any
   * other.
   *
   * "Both" is one request, not `send` followed by `sendText`. Chaining would
   * send two messages and the second call could lose the version race the
   * first one just created. A live link is reused either way.
   */
  const begin = useMutation({
    mutationFn: async (channel: Channel) => ({ channel, fresh: await quoteApi.get(quote.id) }),
    onMutate: () => ({ epoch: sendOutcomeEpoch() }),
    onSuccess: (question) => setAsking(question),
    onError: (error, _channel, context) => publishFailure(error, context?.epoch),
  });

  function publishFailure(error: Error, epoch: number | undefined) {
    publishSendOutcome(
      {
        tone: 'error',
        text: error instanceof ApiRequestError ? error.message : t('quotes.list.sendCheckFailedError'),
      },
      epoch,
    );
  }

  /**
   * Sends at the version the question was asked about, to the recipient it
   * named: a quote that changed while the question was open is refused by
   * the server as a conflict rather than sent to somebody nobody confirmed.
   */
  const send = useMutation({
    mutationFn: async ({ channel, fresh }: { channel: Channel; fresh: Quote }): Promise<SendResult> => {
      if (channel === 'email') {
        const result = await quoteApi.send(fresh.id, fresh.version);
        return { channel: 'email', recipient: result.queuedFor, delivered: result.delivered };
      }
      if (channel === 'text') {
        const result = await quoteApi.sendText(fresh.id, fresh.version);
        if (result.unconfirmed)
          throw new Error(result.failureReason ?? t('messageComposer.unconfirmed', { recipient: result.queuedFor }));
        return {
          channel: 'text',
          recipient: result.queuedFor,
          delivered: result.delivered,
          deferred: result.deferredUntilOpenHours,
        };
      }
      const result = await quoteApi.sendBoth(fresh.id, fresh.version);
      if (result.unconfirmed)
        throw new Error(result.failureReason ?? t('messageComposer.unconfirmed', { recipient: result.textQueuedFor }));
      return {
        channel: 'both',
        emailRecipient: result.emailQueuedFor,
        textRecipient: result.textQueuedFor,
        emailDelivered: result.emailDelivered,
        textDelivered: result.delivered,
        textDeferred: result.deferredUntilOpenHours,
      };
    },
    // Read synchronously when `.mutate()` is called, before the request goes
    // out — a session sign-out or location switch that happens while this is
    // in flight bumps the epoch, and `onSuccess`/`onError` (which React Query
    // still calls, even though the row — and this whole component — may be
    // long unmounted by then) pass the epoch caught here back to
    // `publishSendOutcome`, which drops a result addressed to a boundary that
    // has since closed instead of leaking it into whatever session or
    // location is current when the response actually lands.
    onMutate: () => ({ epoch: sendOutcomeEpoch() }),
    onSuccess: (result, _question, context) => {
      setAsking(null);
      void queryClient.invalidateQueries({ queryKey: ['quotes'] });
      // The row this button lives on is a summary; reopening the full quote
      // right after sending should not hand back the pre-send detail the
      // editor's own 30-second cache is still holding.
      void queryClient.invalidateQueries({ queryKey: ['quote', quote.id] });

      const outcome =
        result.channel === 'email'
          ? result.delivered
            ? { tone: 'success' as const, text: t('quotes.list.sendSuccessEmail', { recipient: result.recipient }) }
            : { tone: 'error' as const, text: t('quotes.list.sendEmailNotDelivered', { recipient: result.recipient }) }
          : result.channel === 'text'
            ? textOutcome(result.recipient, result.delivered, result.deferred)
            : bothOutcome(
                result.emailRecipient,
                result.textRecipient,
                result.emailDelivered,
                result.textDelivered,
                result.textDeferred,
              );
      publishSendOutcome(outcome, context.epoch);
    },
    onError: (error, _question, context) => {
      setAsking(null);
      publishFailure(error, context?.epoch);
    },
  });

  if (!canSend) return null;

  const emailLabel = alreadySent ? t('quotes.list.sendEmailAgain') : t('quotes.list.sendEmail');
  const busy = begin.isPending || send.isPending;

  return (
    <div className={className}>
      {companyCanText ? (
        <SplitButton
          label={emailLabel}
          menuLabel={t('quotes.list.sendMenuLabel')}
          busy={busy}
          onClick={() => begin.mutate('email')}
          actions={[
            {
              key: 'text',
              label: alreadySent ? t('quotes.list.sendTextAgain') : t('quotes.list.sendText'),
              onSelect: () => begin.mutate('text'),
            },
            {
              key: 'both',
              label: t('quotes.list.sendBothAction'),
              hint: t('quotes.list.sendBothActionHint'),
              onSelect: () => begin.mutate('both'),
            },
          ]}
        />
      ) : (
        <Button variant="secondary" busy={busy} onClick={() => begin.mutate('email')}>
          {emailLabel}
        </Button>
      )}
      {asking ? <SendQuestion asking={asking} send={send} onCancel={() => setAsking(null)} /> : null}
    </div>
  );
}

/**
 * The question itself: which quote, by which channel, to whom. A channel the
 * quote has no address or number for is said so here, with no way to confirm,
 * rather than left for the server to refuse after the click.
 */
function SendQuestion({
  asking: { channel, fresh },
  send,
  onCancel,
}: {
  asking: { channel: Channel; fresh: Quote };
  send: { isPending: boolean; mutate: (question: { channel: Channel; fresh: Quote }) => void };
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const number = fresh.quoteNumber;
  // From the read the question was built on, not the row, which can be stale.
  const alreadySent = fresh.status === 'sent' || fresh.status === 'viewed';
  const email = fresh.customerEmailSnapshot?.trim() || null;
  const phone = fresh.customerPhoneSnapshot?.trim() || null;
  const noEmail = channel !== 'text' && !email;
  const noPhone = channel !== 'email' && !phone;
  const missing = noEmail || noPhone;

  const title = missing
    ? t('quotes.list.sendConfirmMissingTitle', { number })
    : channel === 'email'
      ? t(alreadySent ? 'quotes.list.sendConfirmEmailAgainTitle' : 'quotes.list.sendConfirmEmailTitle', {
          number,
          recipient: email,
        })
      : channel === 'text'
        ? t(alreadySent ? 'quotes.list.sendConfirmTextAgainTitle' : 'quotes.list.sendConfirmTextTitle', {
            number,
            recipient: phone,
          })
        : t('quotes.list.sendConfirmBothTitle', { number });

  return (
    <ConfirmDialog
      title={title}
      confirmLabel={t(
        channel === 'email'
          ? 'quotes.list.sendConfirmEmail'
          : channel === 'text'
            ? 'quotes.list.sendConfirmText'
            : 'quotes.list.sendConfirmBoth',
      )}
      confirmDisabled={missing}
      busy={send.isPending}
      onConfirm={() => send.mutate({ channel, fresh })}
      onCancel={onCancel}
    >
      {noEmail ? <p>{t('quotes.list.sendConfirmNoEmail')}</p> : null}
      {noPhone ? <p>{t('quotes.list.sendConfirmNoPhone')}</p> : null}
      {!missing && channel === 'both' ? (
        <p>{t('quotes.list.sendConfirmBothBody', { emailRecipient: email, textRecipient: phone })}</p>
      ) : null}
      {!missing && alreadySent ? <p>{t('quotes.list.sendConfirmAgainNote')}</p> : null}
    </ConfirmDialog>
  );
}
